import { BulkStockErrorFragment } from "@saleor/fragments/types/BulkStockErrorFragment";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { StockErrorFragment } from "@saleor/fragments/types/StockErrorFragment";
import { decimal, joinDateTime, splitDateTime, weight } from "@saleor/misc";
import { ProductUpdatePageSubmitData } from "@saleor/products/components/ProductUpdatePage";
import { ProductDetails_product } from "@saleor/products/types/ProductDetails";
import { ProductImageCreateVariables } from "@saleor/products/types/ProductImageCreate";
import { ProductImageReorderVariables } from "@saleor/products/types/ProductImageReorder";
import {
  ProductSetAvailabilityForPurchase,
  ProductSetAvailabilityForPurchaseVariables
} from "@saleor/products/types/ProductSetAvailabilityForPurchase";
import {
  ProductUpdate,
  ProductUpdateVariables
} from "@saleor/products/types/ProductUpdate";
import { ProductVariantCreateData_product } from "@saleor/products/types/ProductVariantCreateData";
import { ProductVariantDetails_productVariant_product } from "@saleor/products/types/ProductVariantDetails";
import { ProductVariantReorderVariables } from "@saleor/products/types/ProductVariantReorder";
import { RecommendProductReorderVariables } from "@saleor/products/types/RecommendProductReorder";
import {
  SimpleProductUpdate,
  SimpleProductUpdateVariables
} from "@saleor/products/types/SimpleProductUpdate";
import { mapFormsetStockToStockInput } from "@saleor/products/utils/data";
import { getProductAvailabilityVariables } from "@saleor/products/utils/handlers";
import { ReorderEvent } from "@saleor/types";
import {
  RecommendationTargetTypeEnum,
  RecommendationTypeEnum
} from "@saleor/types/globalTypes";
import { MutationFetchResult } from "react-apollo";
import { arrayMove } from "react-sortable-hoc";

export function createUpdateHandler(
  product: ProductDetails_product,
  updateProduct: (
    variables: ProductUpdateVariables
  ) => Promise<MutationFetchResult<ProductUpdate>>,
  updateSimpleProduct: (
    variables: SimpleProductUpdateVariables
  ) => Promise<MutationFetchResult<SimpleProductUpdate>>,
  setProductAvailability: (
    variables: ProductSetAvailabilityForPurchaseVariables
  ) => Promise<MutationFetchResult<ProductSetAvailabilityForPurchase>>
) {
  return async (data: ProductUpdatePageSubmitData) => {
    const productVariables: ProductUpdateVariables = {
      id: product.id,
      input: {
        attributes: data.attributes.map(attribute => ({
          id: attribute.id,
          values: attribute.value[0] === "" ? [] : attribute.value
        })),
        basePrice: decimal(data.basePrice),
        category: data.category,
        chargeTaxes: data.chargeTaxes,
        collections: data.collections,
        descriptionJson: data.descriptionJson,
        description: data.description,
        USP: data.USP,
        care: data.care,
        isPublished: data.isPublished,
        name: data.name,
        publicationDate:
          data.publicationDate !== "" ? data.publicationDate : null,
        seo: {
          description: data.seoDescription,
          title: data.seoTitle,
          keyword: data.seoKeyword
        },
        slug: data.slug,
        taxCode: data.changeTaxCode ? data.taxCode : null,
        visibleInListings: data.visibleInListings,
        lensTypeTemplate: data.lensTypeTemplate
      }
    };

    let errors: Array<
      ProductErrorFragment | StockErrorFragment | BulkStockErrorFragment
    >;

    if (product.productType.hasVariants) {
      const result = await updateProduct(productVariables);
      errors = result.data.productUpdate.errors;
    } else {
      const result = await updateSimpleProduct({
        ...productVariables,
        addStocks: data.addStocks.map(mapFormsetStockToStockInput),
        deleteStocks: data.removeStocks,
        input: {
          ...productVariables.input,
          weight: weight(data.weight)
        },
        productVariantId: product.variants[0].id,
        productVariantInput: {
          sku: data.sku,
          trackInventory: data.trackInventory
        },
        updateStocks: data.updateStocks.map(mapFormsetStockToStockInput)
      });
      errors = [
        ...result.data.productUpdate.errors,
        ...result.data.productVariantStocksCreate.errors,
        ...result.data.productVariantStocksDelete.errors,
        ...result.data.productVariantStocksUpdate.errors,
        ...result.data.productVariantUpdate.errors
      ];
    }
    const {
      isPreOrder,
      preOrderDate,
      preOrderTime,
      isAvailableForPurchase,
      availableForPurchaseDate,
      availableForPurchaseTime
    } = data;
    if (
      isPreOrder !== product.isPreOrder ||
      preOrderDate !== splitDateTime(product.preOrder).date ||
      preOrderTime !== splitDateTime(product.preOrder).time ||
      isAvailableForPurchase !==
        (product.isAvailableForPurchase || !!product.availableForPurchase) ||
      availableForPurchaseDate !==
        splitDateTime(product.availableForPurchase).date ||
      availableForPurchaseTime !==
        splitDateTime(product.availableForPurchase).time
    ) {
      const variables = getProductAvailabilityVariables({
        availableForPurchase: joinDateTime(
          availableForPurchaseDate,
          availableForPurchaseTime
        ),
        isAvailableForPurchase,
        productId: product.id,
        preOrder: isPreOrder ? joinDateTime(preOrderDate, preOrderTime) : "",
        isPreOrder
      });

      const availabilityResult = await setProductAvailability(variables);
      errors = [
        ...errors,
        ...availabilityResult.data.productSetAvailabilityForPurchase.errors
      ];
    }

    return errors;
  };
}

export function createImageUploadHandler(
  id: string,
  createProductImage: (variables: ProductImageCreateVariables) => void
) {
  return (file: File) =>
    createProductImage({
      alt: "",
      image: file,
      product: id
    });
}

export function createImageReorderHandler(
  product: ProductDetails_product,
  reorderProductImages: (variables: ProductImageReorderVariables) => void
) {
  return ({ newIndex, oldIndex }: ReorderEvent) => {
    let ids = product.imagesDas.map(image => image.id);
    ids = arrayMove(ids, oldIndex, newIndex);
    reorderProductImages({
      imagesIds: ids,
      productId: product.id
    });
  };
}

export function createVariantReorderHandler(
  product:
    | ProductDetails_product
    | ProductVariantDetails_productVariant_product
    | ProductVariantCreateData_product,
  reorderProductVariants: (variables: ProductVariantReorderVariables) => void
) {
  return ({ newIndex, oldIndex }: ReorderEvent) => {
    reorderProductVariants({
      move: {
        id: product.variants[oldIndex].id,
        sortOrder: newIndex - oldIndex
      },
      productId: product.id
    });
  };
}

export function createRecommendProductReorderHandler(
  product: ProductDetails_product,
  reorderRecommendProducts: (
    variables: RecommendProductReorderVariables
  ) => void
) {
  return ({ newIndex, oldIndex }: ReorderEvent) => {
    reorderRecommendProducts({
      move: {
        productId: product.recommendProducts[oldIndex].id,
        sortOrder: newIndex - oldIndex
      },
      targetId: product.id,
      targetType: RecommendationTargetTypeEnum.PRODUCT,
      recommendType: RecommendationTypeEnum.RECOMMEND
    });
  };
}

export function createAlsoLikeProductReorderHandler(
  product: ProductDetails_product,
  reorderRecommendProducts: (
    variables: RecommendProductReorderVariables
  ) => void
) {
  return ({ newIndex, oldIndex }: ReorderEvent) => {
    reorderRecommendProducts({
      move: {
        productId: product.recommendProducts[oldIndex].id,
        sortOrder: newIndex - oldIndex
      },
      targetId: product.id,
      targetType: RecommendationTargetTypeEnum.PRODUCT,
      recommendType: RecommendationTypeEnum.ALSO_LIKE
    });
  };
}
